import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useAtomValue } from 'jotai'
import { useTranslate } from '@repo/unicity'
import { useCurrentLanguage } from '@/core/utils'
import { feelgreatCountryAtom } from '@/core/store/country.store'
import { color } from '@/core/theme/tokens'
import { taiwanFormatDataAtom, taiwanShipToAtom } from '../../../stores/taiwan.store'

// Figma 689:5733 — "Standard delivery -" uses raw #003764 (design style, no scale token).
const DELIVERY_TEXT = '#003764'

const asRecord = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' ? (v as Record<string, unknown>) : {})

// Server-calculated freight for the active session (flat $150 for TW).
const readShipping = (format: Record<string, unknown> | null): number => {
    const marketing = asRecord(asRecord(asRecord(format ?? {}).hydra).marketing)
    const freight = asRecord(asRecord(marketing.terms).freight)
    return typeof freight.amount === 'number' ? freight.amount : 0
}

const localText = (v: unknown, language: string): string => {
    const r = asRecord(v)
    const preferred = language.toUpperCase() === 'EN' ? r.english : r.native
    return (typeof preferred === 'string' && preferred) || (typeof r.english === 'string' ? r.english : '') || (typeof v === 'string' ? v : '')
}

const joinParts = (...parts: string[]): string =>
    parts
        .map(p => p.trim())
        .filter(Boolean)
        .join(' ')

// Ship-to + shipping-method summary shown on the payment step. Prefers the
// structured ship-to captured at submit time (taiwanShipToAtom) so the address
// lines are clean; falls back to the format's pre-joined `ship_to.address`
// string (a hard refresh loses the captured parts).
export default function TaiwanShipToSection() {
    const { translate } = useTranslate()
    const language = useCurrentLanguage()
    const navigate = useNavigate()
    const format = useAtomValue(taiwanFormatDataAtom)
    const shipTo = useAtomValue(taiwanShipToAtom)
    const fgCountry = useAtomValue(feelgreatCountryAtom)

    const symbolField = fgCountry?.config?.currency?.currency_symbol
    const symbol = (language.toUpperCase() === 'EN' ? symbolField?.english : symbolField?.native) || symbolField?.english || '$'
    const shipping = readShipping(format)

    const { name, addressLines, email, phone, uuid } = useMemo(() => {
        const u23 = asRecord(asRecord(asRecord(format ?? {}).ushop).u23)
        const detail = asRecord(u23.formatDetail)
        const fmtShipTo = asRecord(detail.ship_to)
        const shipping = asRecord(u23.formatShipping)
        const fmtUuid = typeof asRecord(format ?? {}).uuid === 'string' ? (asRecord(format ?? {}).uuid as string) : ''

        // Structured (preferred): street on one line, city + zip on the next.
        if (shipTo) {
            return {
                name: shipTo.name,
                addressLines: [shipTo.address1, joinParts(shipTo.city, shipTo.zip)].filter(Boolean),
                email: shipTo.email,
                phone: shipTo.phone,
                uuid: fmtUuid
            }
        }

        // Fallback to the format's single (jammed) address string.
        return {
            name: localText(fmtShipTo.name, language),
            addressLines: [localText(fmtShipTo.address, language)].filter(Boolean),
            email: typeof shipping.email === 'string' ? shipping.email : '',
            phone: typeof shipping.phone === 'string' ? shipping.phone : '',
            uuid: fmtUuid
        }
    }, [format, shipTo, language])

    const goToShipping = () => navigate(`/checkout${uuid ? `?uuid=${uuid}` : ''}`)

    return (
        <Stack spacing={3} data-testid="taiwan-payment-ship-to">
            <Stack spacing={2} data-testid="taiwan-payment-ship-to-user-info">
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={{ fontSize: 18, lineHeight: '28px', fontWeight: 600, color: color.blue[600] }} data-testid="taiwan-payment-ship-to-label">
                        {translate('ship_to')}:
                    </Typography>
                    <Typography
                        onClick={goToShipping}
                        sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: color.blue[600], textDecoration: 'underline', cursor: 'pointer' }}
                        data-testid="taiwan-payment-ship-to-change">
                        {translate('change')}
                    </Typography>
                </Stack>

                <Stack spacing={0} data-testid="taiwan-payment-ship-to-details">
                    {name ? (
                        <Typography
                            sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: color.blue[600] }}
                            data-testid="taiwan-payment-ship-to-name">
                            {name}
                        </Typography>
                    ) : null}
                    {addressLines.map((line, i) => (
                        <Typography
                            key={i}
                            sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: color.blue[600] }}
                            data-testid="taiwan-payment-ship-to-address">
                            {line}
                        </Typography>
                    ))}
                </Stack>

                <Stack spacing={0} data-testid="taiwan-payment-ship-to-contact">
                    {email ? (
                        <Typography
                            sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: color.blue[600] }}
                            data-testid="taiwan-payment-ship-to-email">
                            {email}
                        </Typography>
                    ) : null}
                    {phone ? (
                        <Typography
                            sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: color.blue[600] }}
                            data-testid="taiwan-payment-ship-to-phone">
                            {phone}
                        </Typography>
                    ) : null}
                </Stack>
            </Stack>

            <Divider />

            <Stack spacing={2} data-testid="taiwan-payment-shipping-method">
                <Typography sx={{ fontSize: 18, lineHeight: '28px', fontWeight: 600, color: color.blue[600] }}>{translate('shipping_method')}:</Typography>
                <Typography sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 400, color: DELIVERY_TEXT }} data-testid="taiwan-payment-shipping-method-value">
                    {translate('standard_delivery')}
                    {' - '}
                    {shipping > 0 ? (
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {symbol}
                            {Math.round(shipping).toLocaleString('en-US')}
                        </Box>
                    ) : (
                        <Box component="span" sx={{ fontWeight: 700, color: color.orange[500], textTransform: 'uppercase' }}>
                            {translate('free')}
                        </Box>
                    )}
                </Typography>
            </Stack>
        </Stack>
    )
}
